import { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Stack, Grid } from '@mui/material';
import dayjs from 'dayjs';
import TodoCard from './TodoCard';
import HistoryCard from './HistoryCard';
import ProjectDetailsSidebar from './ProjectDetailsSidebar';
import WorkspaceSidebar from './WorkspaceSidebar/WorkspaceSidebar';
import { sxConfig } from './workspace.style';
import { AutoCompleteSelectUI, ConfirmDialogUI, FormLabelUI } from '../../Interface';
import useParam from '../../../context/ParamContext';
import workspaceService from '../../../service/CRM/workspace.service';
import { useLoader } from '../../../provider/LoaderProvider';
import { generateAutocompleteItems } from '../../../utils/helper';
import CrmTaskDialog from './CrmTaskDialog/CrmTaskDialog';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import projectService from '../../../service/project.service';
import WorkspaceEditableText from './WorkspaceEditableText';
import { TagTarget, TagType } from '../../../config/tag';
import tagService from '../../../service/tag.service';

export const WorkspacePage = ({ workspaceState, selectedProjectId, setCreateWorkspaceDialog }) => {
  const { showLoader, hideLoader } = useLoader();
  const { params } = useParam();
  const { autoCompleteOptions, autoCompleteData, projectTagLabels, setProjectTagLabels } = workspaceState;
  const [selectedValues, setSelectedValues] = useState({});
  const [refetch, setRefetch] = useState(false);

  const defCrmTaskDialogData = {
    open: false,
    projectId: null,
    companyId: null,
  };
  const [crmTaskDialogData, setCrmTaskDialogData] = useState(defCrmTaskDialogData);

  const defConfirmDialogData = {
    open: false,
    message: '',
    handleChangeElement: () => {},
  };
  const [confirmDialogData, setConfirmDialogData] = useState(defConfirmDialogData);

  useEffect(() => {
    getProjectById();
  }, [selectedProjectId]);

  useEffect(() => {
    if (refetch) getProjectById();
  }, [refetch]);

  const showProjectTypeStatus = params.PROJECT_SHOW_PROJECT_TYPE_STATUS === 'Y';

  const statusFieldName = showProjectTypeStatus ? 'projectTypeStatusId' : 'statusId';

  const filteredStatusOptions = useMemo(() => {
    if (!showProjectTypeStatus) {
      return autoCompleteOptions.status;
    }

    const statusForProjectType = autoCompleteData?.status?.filter(
      (status) => status.projectTypeId === selectedValues.projectTypeId
    );

    return generateAutocompleteItems(statusForProjectType, 'name', 'id');
  }, [selectedValues.projectTypeId, params]);

  const getProjectById = () => {
    if (!selectedProjectId) {
      setSelectedValues({});
      return;
    }

    showLoader();
    workspaceService
      .getWorkspaceData(selectedProjectId)
      .then((data) => {
        setSelectedValues(data);
        setRefetch(false);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleNewCrmTask = () => {
    setCrmTaskDialogData({
      ...defCrmTaskDialogData,
      open: true,
      projectId: selectedValues.projectId,
      companyId: selectedValues.companyId,
    });
  };

  const handleUpdateField = (field, value) => {
    if (!selectedValues?.projectId) return;

    return projectService.updateProjectField({ [field]: value }, selectedValues.projectId).then((data) => {
      setSelectedValues((prev) => ({
        ...prev,
        [field]: data[field],
      }));

      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const handleResponsibleChange = (_, option) => {
    if (option?.value === selectedValues?.responsiblePersonId) return;

    handleUpdateField('responsiblePersonId', option?.value ?? null);
  };

  const handleStatusChange = (_, option) => {
    if (option?.value === selectedValues?.[statusFieldName]) return;

    handleUpdateField(statusFieldName, option?.value);
  };

  const onCloseConfirmDialog = () => {
    setConfirmDialogData(defConfirmDialogData);
  };

  const handleUpdateProjectTag = async (tagId) => {
    const data = await projectService.updateProjectTag(selectedProjectId, tagId);

    setSelectedValues((prev) => ({
      ...prev,
      tagProjectLink: data.tagProjectLink,
    }));

    addNotification({
      content: 'Sikeres módosítás!',
      type: NotificationType.SUCCESS,
    });
  };

  const handleCreateTag = async (data) => {
    const insertObj = {
      ...data,
      tagTarget: TagTarget.PROJECT,
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObj));

    tagService.createItem(formData).then((data) => {
      addNotification({
        content: 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });

      setProjectTagLabels((prev) => [...prev, data]);
    });
  };

  const handleUpdateTag = async (tagId, data) => {
    tagService.updateTagField(data, tagId).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setProjectTagLabels((prev) => prev.map((tag) => (tag.id === tagId ? data : tag)));

      setSelectedValues((prev) => ({
        ...prev,
        tagProjectLink: prev.tagProjectLink?.map((link) => (link.tagId === tagId ? { ...link, tag: data } : link)),
      }));
    });
  };

  const handleDeleteTag = async (tagId) => {
    tagService.deleteItem(tagId).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });

      setProjectTagLabels((prev) => prev.filter((tag) => tag.id !== tagId));

      setSelectedValues((prev) => ({
        ...prev,
        tagProjectLink: prev.tagProjectLink?.filter((link) => link.tagId !== tagId),
      }));

      onCloseConfirmDialog();
    });
  };

  const handleConfirmDeleteTag = (tagId) => {
    setConfirmDialogData({
      open: true,
      message:
        'Biztosan törölni szeretné a címkét? A törölt címke a továbbiakban nem jelenik meg a hozzárendelt projekteken.',
      handleChangeElement: () => handleDeleteTag(tagId),
    });
  };

  const handleContactAdded = (contact) => {
    setSelectedValues((prev) => ({
      ...prev,
      company: {
        ...prev.company,
        companyContact: [...(prev.company?.companyContact || []), contact],
      },
    }));
  };

  return (
    <>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} lg={10}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper sx={{ ...sxConfig.card, px: 3, py: 2.5 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <WorkspaceEditableText
                      value={selectedValues.projectName}
                      onSave={(value) => handleUpdateField('projectName', value)}
                      sx={{
                        typography: 'h5',
                        fontWeight: 700,
                        flex: 1,
                        minWidth: 0,
                        overflowWrap: 'anywhere',
                      }}
                    />

                    <Box sx={{ width: { xs: '100%', md: 250 }, flexShrink: 0 }}>
                      <AutoCompleteSelectUI
                        label={<FormLabelUI text="Státusz" />}
                        variant="outlined"
                        size="small"
                        dataset={filteredStatusOptions}
                        selectedValue={selectedValues[statusFieldName]}
                        disableClearable
                        onChange={handleStatusChange}
                      />
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} color="text.secondary" sx={{ flexShrink: 0 }}>
                    <Typography>Módosítva:</Typography>
                    <Typography>{dayjs(selectedValues.updatedAt).format('YYYY-MM-DD HH:mm')}</Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Stack spacing={2}>
                <TodoCard
                  valuesState={{ selectedValues, setSelectedValues }}
                  autoCompleteOptions={autoCompleteOptions}
                  autoCompleteData={autoCompleteData}
                  setRefetch={setRefetch}
                  handleNewCrmTask={handleNewCrmTask}
                  setConfirmDialogData={setConfirmDialogData}
                  onCloseConfirmDialog={onCloseConfirmDialog}
                />
                <HistoryCard handleNewCrmTask={handleNewCrmTask} />
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <ProjectDetailsSidebar
                autoCompleteOptions={autoCompleteOptions}
                selectedValues={selectedValues}
                handleResponsibleChange={handleResponsibleChange}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={2}>
          <WorkspaceSidebar
            selectedValues={selectedValues}
            projectTagLabels={projectTagLabels}
            handleUpdateProjectTag={handleUpdateProjectTag}
            handleCreateTag={handleCreateTag}
            handleUpdateTag={handleUpdateTag}
            handleDeleteTag={handleConfirmDeleteTag}
            setCreateWorkspaceDialog={setCreateWorkspaceDialog}
            handleContactAdded={handleContactAdded}
          />
        </Grid>
      </Grid>

      <CrmTaskDialog
        dialogData={crmTaskDialogData}
        handleClose={() => setCrmTaskDialogData(defCrmTaskDialogData)}
        autoCompleteOptions={autoCompleteOptions}
        autoCompleteData={autoCompleteData}
        setRefetch={setRefetch}
      />

      <ConfirmDialogUI
        handleChangeElement={confirmDialogData.handleChangeElement}
        show={confirmDialogData.open}
        onHide={() => onCloseConfirmDialog()}
        message={confirmDialogData.message}
      />
    </>
  );
};
